import { useEffect, useState, useCallback, isValidElement } from "react"
import { useParams, useNavigate, Link } from "react-router-dom"
import { ArrowLeft, Download, ExternalLink, Pencil } from "lucide-react"
import { fr, paymentMethodLabel, paymentTypeLabel } from "../../locales/fr"
import { getPayment, downloadPaymentInvoice } from "../../services/paymentService"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/Card"
import { Button } from "../../components/ui/Button"
import { Badge } from "../../components/ui/Badge"
import { PaymentInvoiceModal } from "../../components/payments/PaymentInvoiceModal"

const cardClass =
  "border border-slate-700 bg-slate-900 text-slate-100 shadow-sm [&_.text-muted-foreground]:text-slate-400"

function InfoRow({ label, value }) {
  const display = isValidElement(value)
    ? value
    : value != null && String(value).trim() !== ""
      ? value
      : "—"
  return (
    <div className="grid grid-cols-1 gap-1 border-b border-slate-700/60 py-3 last:border-0 sm:grid-cols-3 sm:items-center">
      <dt className="text-sm font-medium text-slate-400">{label}</dt>
      <dd className="text-sm text-slate-100 sm:col-span-2">{display}</dd>
    </div>
  )
}

function methodBadge(method) {
  const key = method?.toLowerCase()
  switch (key) {
    case "cash":
      return <Badge variant="success">{fr.paymentMethod.cash}</Badge>
    case "card":
      return <Badge variant="default">{fr.paymentMethod.card}</Badge>
    case "transfer":
      return <Badge variant="secondary">{fr.paymentMethod.transfer}</Badge>
    default:
      return <Badge variant="outline">{paymentMethodLabel(method)}</Badge>
  }
}

export default function PaymentView() {
  const { id } = useParams()
  const navigate = useNavigate()
  const d = fr.payments.detail
  const [payment, setPayment] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false)
  const [downloadError, setDownloadError] = useState("")

  const load = useCallback(() => {
    if (!id) return
    setLoading(true)
    setError("")
    getPayment(id)
      .then(setPayment)
      .catch(() => {
        setPayment(null)
        setError(fr.payments.detail.loadError)
      })
      .finally(() => setLoading(false))
  }, [id])

  useEffect(() => {
    load()
  }, [load])

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-slate-400">
        {fr.common.loading}
      </div>
    )
  }

  if (error || !payment) {
    return (
      <div className="mx-auto max-w-lg space-y-4 text-center text-slate-100">
        <p className="text-slate-300">{error || d.notFound}</p>
        <Button variant="outline" size="sm" onClick={() => navigate("/payments")}>
          <ArrowLeft className="h-4 w-4" />
          {d.back}
        </Button>
      </div>
    )
  }

  const sub = payment.subscription
  const memberId = payment.member?.id

  return (
    <div className="mx-auto max-w-3xl space-y-6 text-slate-100">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <Button variant="outline" size="sm" onClick={() => navigate("/payments")}>
          <ArrowLeft className="h-4 w-4" />
          {d.back}
        </Button>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-2 border-slate-600 text-slate-100 hover:bg-slate-800"
            onClick={async () => {
              setDownloadError("")
              try {
                await downloadPaymentInvoice(payment.id)
              } catch {
                setDownloadError(fr.invoice.downloadError)
              }
            }}
          >
            <Download className="h-4 w-4 shrink-0" />
            {fr.invoice.downloadPdf}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-slate-300 hover:bg-slate-800 hover:text-slate-50"
            onClick={() => setInvoiceModalOpen(true)}
          >
            <Pencil className="mr-2 h-4 w-4" />
            {fr.invoice.editInvoice}
          </Button>
        </div>
      </div>

      <div>
        <h1 className="text-2xl font-bold tracking-tight text-slate-50">
          {d.title} #{payment.id}
        </h1>
        <p className="mt-1 text-sm text-slate-400">{d.subtitle}</p>
      </div>

      {downloadError ? (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-red-300">
          {downloadError}
        </div>
      ) : null}

      <Card className={cardClass}>
        <CardHeader className="border-b border-slate-700 pb-4">
          <CardTitle className="text-slate-50">{d.invoiceSection}</CardTitle>
          <CardDescription className="!text-slate-400">
            {payment.invoiceNumber || `REF-${payment.id}`}
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          <dl>
            <InfoRow label={fr.payments.table.amount} value={`${payment.amount} MAD`} />
            <InfoRow label={fr.payments.table.method} value={methodBadge(payment.method)} />
            <InfoRow
              label={fr.tables.paymentType}
              value={paymentTypeLabel(payment.paymentType)}
            />
            <InfoRow label={fr.payments.table.date} value={payment.createdAt} />
            <InfoRow label={fr.tables.invoice} value={payment.invoiceNumber || `REF-${payment.id}`} />
            {payment.invoiceNotes ? (
              <InfoRow label={d.notesLabel} value={payment.invoiceNotes} />
            ) : null}
          </dl>
        </CardContent>
      </Card>

      <Card className={cardClass}>
        <CardHeader className="border-b border-slate-700 pb-4">
          <CardTitle className="text-slate-50">{d.memberSection}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4 pt-6 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-lg font-medium text-slate-100">{payment.member?.name || "—"}</p>
          {memberId ? (
            <Link
              to={`/members/${memberId}`}
              className="inline-flex h-9 items-center justify-center gap-2 rounded-md border border-slate-600 px-3 text-sm font-medium text-slate-100 transition-colors hover:bg-slate-800"
            >
              <ExternalLink className="h-4 w-4 shrink-0" />
              {d.memberLink}
            </Link>
          ) : null}
        </CardContent>
      </Card>

      <Card className={cardClass}>
        <CardHeader className="border-b border-slate-700 pb-4">
          <CardTitle className="text-slate-50">{d.subscriptionSection}</CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          {!sub ? (
            <p className="text-sm text-slate-400">{d.noSubscription}</p>
          ) : (
            <dl>
              <InfoRow label={fr.subscriptions.table.pack} value={sub.pack} />
              <InfoRow label={fr.subscriptions.table.plan} value={sub.plan} />
              <InfoRow
                label={fr.subscriptions.table.price}
                value={sub.priceTotal != null ? `${sub.priceTotal} MAD` : "—"}
              />
            </dl>
          )}
        </CardContent>
      </Card>

      <PaymentInvoiceModal
        open={invoiceModalOpen}
        onClose={() => setInvoiceModalOpen(false)}
        paymentId={payment.id}
        onSaved={load}
      />
    </div>
  )
}
